<template>
    <div class="sellers-list-page">
        <div class="page-header">
            <div class="header-left">
                <h1>Mes vendeurs</h1>
                <p class="subtitle">{{ totalSellers }} vendeur(s) assigné(s)</p>
            </div>
            <div class="header-actions">
                <div class="search-box">
                    <i class="material-icons">search</i>
                    <input
                        type="text"
                        v-model="searchQuery"
                        placeholder="Rechercher un vendeur..."
                        @input="debouncedSearch"
                    />
                </div>
                <select v-model="countryFilter" class="country-filter" @change="loadSellers">
                    <option value="">Tous les pays</option>
                    <option v-for="country in countries" :key="country.code" :value="country.code">
                        {{ country.name }}
                    </option>
                </select>
            </div>
        </div>

        <!-- Sellers Grid -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <span>Chargement des vendeurs...</span>
        </div>

        <div v-else-if="sellers.length === 0" class="empty-state">
            <i class="material-icons">person_off</i>
            <h3>Aucun vendeur trouvé</h3>
            <p v-if="searchQuery || countryFilter">Essayez de modifier vos filtres</p>
            <p v-else>Vous n'avez pas encore de vendeurs assignés</p>
        </div>

        <div v-else class="sellers-grid">
            <div
                v-for="seller in sellers"
                :key="seller._id"
                class="seller-card"
                @click="viewSellerStats(seller._id)"
            >
                <div class="card-header">
                    <div class="seller-avatar">
                        <img v-if="seller.avatar" :src="seller.avatar" :alt="seller.fullName" />
                        <span v-else>{{ getInitial(seller) }}</span>
                    </div>
                    <div class="online-status" :class="{ online: isOnline(seller._id) }"></div>
                </div>

                <div class="card-body">
                    <h3 class="seller-name">{{ seller.fullName || seller.email }}</h3>
                    <p class="seller-email">{{ seller.email }}</p>
                    <div class="seller-meta">
                        <span class="country-badge">
                            <i class="material-icons">location_on</i>
                            {{ seller.country || 'N/A' }}
                        </span>
                        <span class="assigned-date">
                            Assigné {{ formatDate(seller.accountManagerAssignedAt) }}
                        </span>
                    </div>
                </div>

                <div class="card-footer">
                    <div class="stat">
                        <span class="stat-value">{{ seller.ordersCount || 0 }}</span>
                        <span class="stat-label">Commandes</span>
                    </div>
                    <div class="stat">
                        <span class="stat-value">{{ seller.productsCount || 0 }}</span>
                        <span class="stat-label">Produits</span>
                    </div>
                    <div class="stat">
                        <span class="stat-value">{{ formatRevenue(seller.totalRevenue) }}</span>
                        <span class="stat-label">Revenus</span>
                    </div>
                </div>

                <div class="card-actions">
                    <button class="action-btn chat" @click.stop="startChat(seller)">
                        <i class="material-icons">chat</i>
                        <span>Chat</span>
                        <span v-if="getUnreadCount(seller._id) > 0" class="unread-badge">
                            {{ getUnreadCount(seller._id) }}
                        </span>
                    </button>
                    <button class="action-btn stats" @click.stop="viewSellerStats(seller._id)">
                        <i class="material-icons">bar_chart</i>
                        <span>Stats</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
            <button
                class="page-btn"
                :disabled="currentPage === 1"
                @click="changePage(currentPage - 1)"
            >
                <i class="material-icons">chevron_left</i>
            </button>
            <span class="page-info">Page {{ currentPage }} sur {{ totalPages }}</span>
            <button
                class="page-btn"
                :disabled="currentPage === totalPages"
                @click="changePage(currentPage + 1)"
            >
                <i class="material-icons">chevron_right</i>
            </button>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';

export default {
    name: 'SellersList',
    data() {
        return {
            sellers: [],
            totalSellers: 0,
            loading: false,
            searchQuery: '',
            countryFilter: '',
            countries: [],
            currentPage: 1,
            pageSize: 12,
            totalPages: 1
        };
    },
    computed: {
        ...mapState('chat', ['onlineUsers', 'conversations'])
    },
    created() {
        this.debouncedSearch = debounce(this.loadSellers, 300);
    },
    async mounted() {
        await this.loadCountries();
        await this.loadSellers();
    },
    methods: {
        async loadCountries() {
            try {
                var response = await this.$server.search('warehouses', { all: true, limit: 100 });
                if (response && response.content && response.content.results) {
                    var countriesMap = {};
                    response.content.results.forEach(function(warehouse) {
                        if (warehouse.country) {
                            countriesMap[warehouse.country] = {
                                code: warehouse.country,
                                name: warehouse.countryName || warehouse.name || warehouse.country
                            };
                        }
                    });
                    this.countries = Object.values(countriesMap).sort(function(a, b) {
                        return a.name.localeCompare(b.name);
                    });
                }
            } catch (error) {
                console.error('Failed to load countries:', error);
                this.countries = [];
            }
        },
        async loadSellers() {
            this.loading = true;
            try {
                const params = {
                    page: this.currentPage,
                    limit: this.pageSize
                };

                if (this.searchQuery) {
                    params.search = this.searchQuery;
                }
                if (this.countryFilter) {
                    params.country = this.countryFilter;
                }

                const response = await this.$server.getAssignedSellers('accountManager', params);

                if (response && response.content) {
                    this.sellers = response.content.results || [];
                    this.totalSellers = response.content.total || 0;
                    this.totalPages = Math.ceil(this.totalSellers / this.pageSize);
                }
            } catch (error) {
                console.error('Failed to load sellers:', error);
                if (this.$toast) this.$toast.error('Erreur lors du chargement des vendeurs');
            } finally {
                this.loading = false;
            }
        },
        getInitial(seller) {
            const name = seller.fullName || seller.firstName || seller.email || '';
            return name.charAt(0).toUpperCase();
        },
        isOnline(sellerId) {
            if (!this.onlineUsers) return false;
            if (this.onlineUsers instanceof Set) {
                return this.onlineUsers.has(sellerId);
            }
            if (Array.isArray(this.onlineUsers)) {
                return this.onlineUsers.includes(sellerId);
            }
            // onlineUsers is an object { oduserId: boolean }
            return this.onlineUsers[sellerId] === true;
        },
        getUnreadCount(sellerId) {
            if (!this.conversations) return 0;
            const conv = this.conversations.find(function(c) {
                return c.seller && c.seller._id === sellerId;
            });
            return (conv && conv.unreadByAM) || 0;
        },
        formatDate(dateStr) {
            if (!dateStr) return 'récemment';
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        formatRevenue(amount) {
            if (!amount) return '0 MAD';
            return new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'MAD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            }).format(amount);
        },
        viewSellerStats(sellerId) {
            this.$router.push(`/account-manager/sellers/${sellerId}`);
        },
        async startChat(seller) {
            try {
                // Find or create conversation
                const response = await this.$server.getOrCreateConversation('conversations', {
                    sellerId: seller._id
                });

                if (response && response.content) {
                    this.$router.push(`/account-manager/chat/${response.content._id}`);
                }
            } catch (error) {
                console.error('Failed to start chat:', error);
                if (this.$toast) this.$toast.error('Erreur lors de l\'ouverture du chat');
            }
        },
        changePage(page) {
            if (page >= 1 && page <= this.totalPages) {
                this.currentPage = page;
                this.loadSellers();
            }
        }
    }
};
</script>

<style scoped>
.sellers-list-page {
    padding: 24px;
}

.page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.header-left h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #111827;
}

.subtitle {
    margin: 4px 0 0;
    color: #6b7280;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
}

.search-box {
    position: relative;
    display: flex;
    align-items: center;
}

.search-box i {
    position: absolute;
    left: 12px;
    color: #9ca3af;
    font-size: 20px;
}

.search-box input {
    padding: 10px 12px 10px 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    width: 260px;
    outline: none;
    transition: border-color 0.2s;
}

.search-box input:focus {
    border-color: #059669;
}

.country-filter {
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    cursor: pointer;
    background-color: white;
}

.country-filter:focus {
    border-color: #059669;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    color: #9ca3af;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #059669;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.empty-state i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.empty-state h3 {
    margin: 0 0 8px;
    font-size: 18px;
    color: #6b7280;
}

.empty-state p {
    margin: 0;
    font-size: 14px;
}

.sellers-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
}

.seller-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
}

.seller-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.card-header {
    position: relative;
    display: flex;
    justify-content: center;
    padding: 24px 20px 0;
}

.seller-avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    overflow: hidden;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 28px;
    font-weight: 600;
}

.seller-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.online-status {
    position: absolute;
    top: 24px;
    right: calc(50% - 36px);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #d1d5db;
    border: 3px solid #ffffff;
}

.online-status.online {
    background-color: #10b981;
}

.card-body {
    padding: 16px 20px;
    text-align: center;
}

.seller-name {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #111827;
}

.seller-email {
    margin: 4px 0 12px;
    font-size: 13px;
    color: #6b7280;
}

.seller-meta {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}

.country-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background-color: #ecfdf5;
    color: #059669;
    border-radius: 16px;
    font-size: 12px;
    font-weight: 500;
}

.country-badge i {
    font-size: 14px;
}

.assigned-date {
    font-size: 12px;
    color: #9ca3af;
}

.card-footer {
    display: flex;
    justify-content: space-around;
    padding: 16px 20px;
    background-color: #f9fafb;
    border-top: 1px solid #e5e7eb;
}

.stat {
    text-align: center;
}

.stat-value {
    display: block;
    font-size: 18px;
    font-weight: 700;
    color: #111827;
}

.stat-label {
    font-size: 11px;
    color: #6b7280;
    text-transform: uppercase;
}

.card-actions {
    display: flex;
    border-top: 1px solid #e5e7eb;
}

.action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: background-color 0.2s;
    position: relative;
}

.action-btn:first-child {
    border-right: 1px solid #e5e7eb;
}

.action-btn.chat {
    color: #059669;
}

.action-btn.chat:hover {
    background-color: #ecfdf5;
}

.action-btn.stats {
    color: #3b82f6;
}

.action-btn.stats:hover {
    background-color: #eff6ff;
}

.action-btn i {
    font-size: 18px;
}

.unread-badge {
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    background-color: #ef4444;
    color: white;
    border-radius: 9px;
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
}

.pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 32px;
}

.page-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
    background-color: #f3f4f6;
    border-color: #d1d5db;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: #6b7280;
}

@media (max-width: 768px) {
    .sellers-list-page {
        padding: 16px;
    }

    .page-header {
        flex-direction: column;
    }

    .header-actions {
        width: 100%;
        flex-direction: column;
    }

    .search-box input {
        width: 100%;
    }

    .country-filter {
        width: 100%;
    }

    .sellers-grid {
        grid-template-columns: 1fr;
    }
}
</style>
